import React, { useEffect, useMemo, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import Header from "../../../components/Header";
import PageContainer from "../../../components/PageContainer";
import FirmFlowTable from "../../../components/mytasks/FirmFlowTable";
import Sidebar from "../../../components/Sidebar";
import { getFilteredClients } from "../../../services/analytics/getFilteredClients";
import { getEmployee } from "../../../services/getEmployee";
import { getServiceDepartments } from "../../../services/service/getServiceDepartments";

const getText = (...values) => {
    const value = values.find(
        (candidate) =>
            typeof candidate === "string" && candidate.trim().length > 0,
    );

    return value?.trim() || "";
};

const getPersonName = (person) =>
    getText(
        person?.employeeName,
        person?.fullName,
        person?.name,
        [person?.firstName, person?.lastName].filter(Boolean).join(" "),
    );

const getResponseLabels = (data, clients) => {
    const firstClient = clients[0];

    return {
        employeeName: getText(
            data?.employeeName,
            getPersonName(data?.employee),
            getPersonName(firstClient?.employee),
            firstClient?.employeeName,
        ),
        departmentName: getText(
            data?.departmentName,
            data?.department?.name,
            data?.department?.departmentName,
            firstClient?.departmentName,
            firstClient?.department?.name,
            firstClient?.department?.departmentName,
        ),
        teamName: getText(
            data?.teamName,
            data?.team?.name,
            data?.team?.teamName,
            firstClient?.teamName,
            firstClient?.team?.name,
            firstClient?.team?.teamName,
        ),
    };
};

function FilteredClients() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadedRequestKey, setLoadedRequestKey] = useState(null);
    const [labels, setLabels] = useState({
        employeeName: "",
        departmentName: "",
        teamName: "",
    });
    const [tooltip, showTooltip] = useState(true);
    const requestSequenceRef = useRef(0);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const employeeId = searchParams.get("employeeId");
    const departmentId = searchParams.get("departmentId");
    const teamId = searchParams.get("teamId");
    const teamRoleId = searchParams.get("teamRoleId");
    const clientTypeParam = searchParams.get("clientType");
    const clientType =
        clientTypeParam === "1" || clientTypeParam === "2"
            ? Number(clientTypeParam)
            : null;
    const includeInactive = searchParams.get("includeInactive") === "true";
    const requestKey = JSON.stringify({
        employeeId,
        departmentId,
        teamId,
        teamRoleId,
        clientType,
        includeInactive,
    });

    useEffect(() => {
        let isMounted = true;
        const requestSequence = ++requestSequenceRef.current;
        setLoading(true);

        const fetchClients = async () => {
            const response = await getFilteredClients({
                employeeId,
                departmentId,
                teamId,
                teamRoleId,
                clientType,
                includeInactive,
            });

            if (
                !isMounted ||
                requestSequence !== requestSequenceRef.current
            ) {
                return;
            }

            if (response.success) {
                const filteredClients = Array.isArray(response.data?.clients)
                    ? response.data.clients
                    : [];
                const responseLabels = getResponseLabels(
                    response.data,
                    filteredClients,
                );
                const [employeeResponse, departmentsResponse] =
                    await Promise.all([
                        employeeId && !responseLabels.employeeName
                            ? getEmployee(employeeId)
                            : Promise.resolve(null),
                        departmentId && !responseLabels.departmentName
                            ? getServiceDepartments()
                            : Promise.resolve(null),
                    ]);

                if (
                    !isMounted ||
                    requestSequence !== requestSequenceRef.current
                ) {
                    return;
                }

                const employee = employeeResponse?.data?.employeeResponse;
                const department =
                    departmentsResponse?.data?.departments?.find(
                        (item) => String(item?.id) === String(departmentId),
                    );

                setClients(filteredClients);
                setLabels({
                    employeeName:
                        responseLabels.employeeName || getPersonName(employee),
                    departmentName:
                        responseLabels.departmentName ||
                        getText(department?.name, department?.departmentName),
                    teamName: responseLabels.teamName,
                });
            } else {
                toast.error("Failed to fetch clients");
            }

            setLoadedRequestKey(requestKey);
            setLoading(false);
        };
        fetchClients();

        return () => {
            isMounted = false;
        };
    }, [
        departmentId,
        employeeId,
        clientType,
        includeInactive,
        requestKey,
        teamId,
        teamRoleId,
    ]);

    const sortedClients = useMemo(
        () =>
            [...clients].sort((firstClient, secondClient) =>
                String(firstClient?.clientName ?? "").localeCompare(
                    String(secondClient?.clientName ?? ""),
                    undefined,
                    { sensitivity: "base" },
                ),
            ),
        [clients],
    );

    const titleLabels = [
        employeeId && labels.employeeName
            ? `Employee: ${labels.employeeName}`
            : "",
        departmentId && labels.departmentName
            ? `Department: ${labels.departmentName}`
            : "",
        teamId && labels.teamName ? `Team: ${labels.teamName}` : "",
    ].filter(Boolean);

    const columns = useMemo(
        () => [
            {
                Header: "Client",
                accessor: "clientName",
                Cell: (props) => (
                    <>
                        {tooltip && (
                            <ReactTooltip
                                type="light"
                                effect="solid"
                                place="top"
                                border={true}
                                borderColor={"rgb(220,220,220)"}
                                className="rounded-md bg-gray-50"
                                multiline={false}
                            />
                        )}
                        <button
                            onMouseEnter={() => showTooltip(true)}
                            onMouseLeave={() => showTooltip(false)}
                            onClick={() =>
                                navigate(
                                    `/clients/manage/${props.row.original.clientId}`
                                )
                            }
                            className="rounded-full text-gray hover:text-blue-500"
                        >
                            {props.row.original.clientName}
                        </button>
                    </>
                ),
            },
        ],
        []
    );

    return (
        <div className="flex h-screen overflow-hidden">
            <Toaster />
            {/* Sidebar */}
            <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

            {/* Content area */}
            <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                {/*  Site header */}
                <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                <PageContainer
                    isLoading={loading || loadedRequestKey !== requestKey}
                    loadingText="Loading clients..."
                >
                    <main>
                        <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                            {/* Page header */}
                            <div className="mb-8 sm:flex sm:justify-between sm:items-center">
                                {/* Left: Title */}
                                <div className="mb-4 sm:mb-0">
                                    <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                        {clients.length} Clients assigned
                                        {titleLabels.length > 0
                                            ? ` to ${titleLabels.join(" ")}`
                                            : ""}
                                    </h1>
                                </div>
                            </div>

                            {/* Table */}
                            <FirmFlowTable
                                data={sortedClients}
                                columns={columns}
                                resetPageSignal={includeInactive}
                            />
                        </div>
                    </main>
                </PageContainer>
            </div>
        </div>
    );
}

export default FilteredClients;
